import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from '../components/customAxios';
import baseUrl from '../../base_url';
import StatusBadge from '../../components/common/StatusBadge';
import {
  ShoppingBag,
  Search,
  Download,
  CheckSquare,
  Square,
  RefreshCw,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Package,
  Truck
} from 'lucide-react';
import toast from 'react-hot-toast';

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || 'ready_to_dispatch';
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [selectedOrders, setSelectedOrders] = useState(new Set());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 15;

  const navigate = useNavigate();

  const fetchOrders = async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${baseUrl}api/admin/get-orders`);
      if (res.data?.data) {
        setOrders(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'ready_to_dispatch', label: 'Needs Dispatch', tooltip: 'Paid orders awaiting packing and carrier dispatch' },
    { id: 'all', label: 'All Orders', tooltip: 'Complete historical registry across all lifecycle statuses' },
    { id: 'in_transit', label: 'In Transit', tooltip: 'Dispatched parcels with courier tracking' },
    { id: 'completed', label: 'Delivered', tooltip: 'Completed orders delivered to customers' },
    { id: 'payment_issues', label: 'Pending Payment', tooltip: 'Uncompleted or failed checkouts' },
    { id: 'high_value', label: 'High Value (>£100)', tooltip: 'Orders exceeding £100 net total' },
    { id: 'cancelled', label: 'Cancelled', tooltip: 'Cancelled and refunded orders' }
  ];

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts = {
      all: orders.length,
      ready_to_dispatch: 0,
      in_transit: 0,
      completed: 0,
      payment_issues: 0,
      high_value: 0,
      cancelled: 0
    };

    orders.forEach(o => {
      const s = (o.status || '').toLowerCase();
      const total = Number(o.pricing?.totalAmount || o.totalAmount || 0);

      if (s === 'placed' || s === 'confirmed' || s === 'processing') counts.ready_to_dispatch++;
      else if (s === 'shipped' || s === 'out_for_delivery') counts.in_transit++;
      else if (s === 'delivered' || s === 'completed') counts.completed++;
      else if (s === 'cancelled' || s === 'canceled') counts.cancelled++;

      if (s === 'pending' || (o.payment?.status && o.payment.status === 'failed')) counts.payment_issues++;
      if (total >= 100) counts.high_value++;
    });

    return counts;
  }, [orders]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const s = (order.status || '').toLowerCase();
      const total = Number(order.pricing?.totalAmount || order.totalAmount || 0);
      const q = searchQuery.toLowerCase().trim();

      // Tab condition
      let matchTab = true;
      if (activeTab === 'ready_to_dispatch') {
        matchTab = s === 'placed' || s === 'confirmed' || s === 'processing';
      } else if (activeTab === 'in_transit') {
        matchTab = s === 'shipped' || s === 'out_for_delivery';
      } else if (activeTab === 'completed') {
        matchTab = s === 'delivered' || s === 'completed';
      } else if (activeTab === 'payment_issues') {
        matchTab = s === 'pending' || (order.payment?.status && order.payment.status === 'failed');
      } else if (activeTab === 'high_value') {
        matchTab = total >= 100;
      } else if (activeTab === 'cancelled') {
        matchTab = s === 'cancelled' || s === 'canceled';
      }

      if (!matchTab) return false;

      // Payment method filter
      if (paymentFilter !== 'all') {
        const method = (order.payment?.method || (order.paymentMethod ? order.paymentMethod[0] : '')).toLowerCase();
        if (paymentFilter === 'stripe' && !method.includes('stripe') && !method.includes('card')) return false;
        if (paymentFilter === 'paypal' && !method.includes('paypal')) return false;
        if (paymentFilter === 'cod' && !method.includes('cod')) return false;
      }

      // Search query condition
      if (q) {
        const orderNum = (order.orderNumber || order._id || '').toLowerCase();
        const email = (order.email || '').toLowerCase();
        const custName = (order.shippingAddress?.fullName || '').toLowerCase();
        const city = (order.shippingAddress?.city || '').toLowerCase();
        const postcode = (order.shippingAddress?.postalCode || '').toLowerCase();
        const tracking = (order.tracking || order.shipping?.trackingNumber || '').toLowerCase();

        const itemMatch = Array.isArray(order.cart) && order.cart.some(it =>
          (it.title && it.title.toLowerCase().includes(q)) ||
          (it.articleCode && it.articleCode.toLowerCase().includes(q))
        );

        return orderNum.includes(q) || email.includes(q) || custName.includes(q) ||
               city.includes(q) || postcode.includes(q) || tracking.includes(q) || itemMatch;
      }

      return true;
    });
  }, [orders, activeTab, paymentFilter, searchQuery]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage) || 1;
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ordersPerPage;
    return filteredOrders.slice(start, start + ordersPerPage);
  }, [filteredOrders, currentPage, ordersPerPage]);

  // Toggle selection
  const toggleSelectAll = () => {
    if (selectedOrders.size === paginatedOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(paginatedOrders.map(o => o._id)));
    }
  };

  const toggleSelectOrder = (id) => {
    const next = new Set(selectedOrders);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedOrders(next);
  };

  // Bulk actions
  const handleBulkExportCSV = () => {
    const selectedList = orders.filter(o => selectedOrders.has(o._id));
    if (selectedList.length === 0) {
      toast.error('No orders selected for export');
      return;
    }

    const headers = ['Order Number', 'Date', 'Customer', 'Email', 'City', 'Postal Code', 'Items Count', 'Total (£)', 'Status', 'Payment Method'];
    const rows = selectedList.map(o => [
      o.orderNumber || o._id,
      new Date(o.createdAt).toISOString(),
      `"${o.shippingAddress?.fullName || ''}"`,
      o.email || '',
      `"${o.shippingAddress?.city || ''}"`,
      o.shippingAddress?.postalCode || '',
      o.cart?.length || 0,
      (o.pricing?.totalAmount || o.totalAmount || 0).toFixed(2),
      o.status,
      o.payment?.method || 'Card'
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `mia_omorfia_orders_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${selectedList.length} orders to CSV`);
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedOrders.size === 0) return;
    try {
      const ids = Array.from(selectedOrders);
      await Promise.all(
        ids.map(id => axios.post(`${baseUrl}api/admin/edit-status/${id}`, { newStatus }))
      );
      toast.success(`Updated ${ids.length} orders to '${newStatus}'`);
      setSelectedOrders(new Set());
      fetchOrders();
    } catch {
      toast.error('Failed to update some orders');
    }
  };

  return (
    <div className="space-y-5 max-w-7xl mx-auto">
      {/* Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl font-bold text-stone-900 tracking-tight">Orders Operations Workspace</h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-stone-100 text-stone-700">
              {orders.length} Total Orders
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500 mt-1 flex-wrap">
            <span className="inline-flex items-center gap-1 font-semibold text-amber-800 bg-amber-50 border border-amber-200/70 px-2 py-0.5 rounded-md text-[11px]" title="Orders paid and awaiting warehouse dispatch">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {tabCounts.ready_to_dispatch} Needs Dispatch
            </span>
            <span className="inline-flex items-center gap-1 font-medium text-stone-600 bg-stone-100/80 border border-stone-200 px-2 py-0.5 rounded-md text-[11px]" title="Incomplete or uncaptured checkouts">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
              {tabCounts.payment_issues} Pending Payment
            </span>
            <span className="inline-flex items-center gap-1 font-medium text-stone-500 bg-stone-50 border border-stone-200 px-2 py-0.5 rounded-md text-[11px]" title="All orders across all statuses">
              <span className="w-1.5 h-1.5 rounded-full bg-stone-400" />
              {tabCounts.all} All Orders
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {selectedOrders.size > 0 && (
            <div className="flex items-center gap-2 bg-stone-900 text-white px-3 py-1.5 rounded-lg text-xs font-semibold animate-in fade-in duration-100">
              <span>{selectedOrders.size} selected</span>
              <button
                onClick={handleBulkExportCSV}
                className="hover:text-amber-300 ml-1 flex items-center gap-1"
                title="Export Selected"
              >
                <Download className="w-3.5 h-3.5" />
                CSV
              </button>
              <button
                onClick={() => handleBulkStatusChange('processing')}
                className="hover:text-amber-300 ml-2"
              >
                Mark Processing
              </button>
            </div>
          )}

          <button
            onClick={fetchOrders}
            disabled={refreshing}
            className="p-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-xs font-medium transition-colors"
            title="Refresh Orders"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Operational Saved Views (Tabs) */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 border-b border-stone-200 text-xs font-medium">
        {tabs.map(tab => {
          const count = tabCounts[tab.id] || 0;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setSearchParams({ tab: tab.id });
                setCurrentPage(1);
              }}
              title={tab.tooltip}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                isActive
                  ? 'bg-stone-900 text-white font-semibold shadow-xs'
                  : 'text-stone-600 hover:bg-stone-200/60'
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-stone-700 text-white' : 'bg-stone-100 text-stone-600'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Multi-Dimensional Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-xl border border-stone-200 shadow-xs">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search order number, customer name, email, UK city, postcode, SKU, tracking..."
            className="w-full pl-9 pr-4 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Payment gateway filter */}
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="bg-stone-50 border border-stone-200 text-stone-700 text-xs rounded-lg px-3 py-2 focus:outline-none"
          >
            <option value="all">All Gateways</option>
            <option value="stripe">Stripe / Card</option>
            <option value="paypal">PayPal</option>
            <option value="cod">Cash on Delivery</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-stone-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-stone-400" />
            <p className="text-xs font-medium text-stone-600">Loading operations queue...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-12 text-center text-stone-400">
            <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-stone-300 stroke-[1.5]" />
            <h3 className="font-semibold text-stone-800 text-sm">No orders matching current filter</h3>
            <p className="text-xs text-stone-400 mt-1 max-w-sm mx-auto">
              Try switching tabs, clearing search terms, or resetting filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-stone-50/80 border-b border-stone-200 text-stone-500 font-semibold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-10 text-center">
                    <button onClick={toggleSelectAll} className="p-0.5 text-stone-400 hover:text-stone-700">
                      {selectedOrders.size === paginatedOrders.length ? (
                        <CheckSquare className="w-4 h-4 text-stone-900" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="p-3.5">Order</th>
                  <th className="p-3.5">Date</th>
                  <th className="p-3.5">Customer & Destination</th>
                  <th className="p-3.5">Items</th>
                  <th className="p-3.5">Financials</th>
                  <th className="p-3.5">Fulfillment</th>
                  <th className="p-3.5">Tracking</th>
                  <th className="p-3.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {paginatedOrders.map((order) => {
                  const isSelected = selectedOrders.has(order._id);
                  const total = Number(order.pricing?.totalAmount || order.totalAmount || 0);
                  const trackingCode = order.tracking || order.shipping?.trackingNumber;
                  const trackingUrl = order.shipping?.trackingUrl;

                  return (
                    <tr
                      key={order._id}
                      className={`hover:bg-amber-50/40 transition-colors ${
                        isSelected ? 'bg-amber-50/60' : ''
                      }`}
                    >
                      {/* Checkbox */}
                      <td className="p-3.5 text-center">
                        <button
                          onClick={() => toggleSelectOrder(order._id)}
                          className="p-0.5 text-stone-400 hover:text-stone-700"
                        >
                          {isSelected ? (
                            <CheckSquare className="w-4 h-4 text-stone-900" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                      </td>

                      {/* Order Number */}
                      <td className="p-3.5 font-mono">
                        <Link
                          to={`/order/${order._id}`}
                          className="font-bold text-stone-900 hover:text-amber-700 hover:underline"
                        >
                          {order.orderNumber || order._id.slice(-8)}
                        </Link>
                      </td>

                      {/* Date */}
                      <td className="p-3.5 text-stone-500 whitespace-nowrap">
                        {formatDate(order.createdAt)}
                      </td>

                      {/* Customer & Location */}
                      <td className="p-3.5">
                        <div className="font-semibold text-stone-900">
                          {order.shippingAddress?.fullName || 'Customer'}
                        </div>
                        <div className="text-stone-500 truncate max-w-xs">{order.email}</div>
                        <div className="text-[11px] text-stone-400 mt-0.5">
                          {order.shippingAddress?.city}, {order.shippingAddress?.postalCode}
                        </div>
                      </td>

                      {/* Items Preview */}
                      <td className="p-3.5">
                        <div className="flex items-center gap-1.5">
                          {Array.isArray(order.cart) && order.cart.slice(0, 2).map((item, i) => (
                            <div key={i} className="relative group">
                              {item.image ? (
                                <img
                                  src={item.image}
                                  alt=""
                                  className="w-8 h-8 rounded object-cover border border-stone-200"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded bg-stone-100 flex items-center justify-center text-stone-400">
                                  <Package className="w-3.5 h-3.5" />
                                </div>
                              )}
                              <span className="absolute -top-1.5 -right-1.5 px-1 py-0.2 rounded-full bg-stone-900 text-[9px] font-bold text-white">
                                {item.quantity}
                              </span>
                            </div>
                          ))}
                          {order.cart?.length > 2 && (
                            <span className="text-[10px] font-semibold text-stone-400">
                              +{order.cart.length - 2}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Financials & Payment */}
                      <td className="p-3.5 whitespace-nowrap">
                        <div className="font-bold text-stone-900">£{total.toFixed(2)}</div>
                        <div className="text-[11px] text-stone-400 capitalize">
                          {order.payment?.method || (order.paymentMethod ? order.paymentMethod[0] : 'Card')}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="p-3.5 whitespace-nowrap">
                        <StatusBadge type="order" status={order.status} />
                      </td>

                      {/* Tracking */}
                      <td className="p-3.5 whitespace-nowrap">
                        {trackingCode ? (
                          trackingUrl ? (
                            <a
                              href={trackingUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 font-mono text-[11px] text-sky-700 hover:underline"
                            >
                              <Truck className="w-3 h-3" />
                              {trackingCode.slice(0, 12)}
                              <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-1 font-mono text-[11px] text-stone-600">
                              <Truck className="w-3 h-3 text-stone-400" />
                              {trackingCode.slice(0, 12)}
                            </span>
                          )
                        ) : (
                          <span className="text-[11px] text-stone-400 italic">No tracking</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="p-3.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/order/${order._id}`)}
                          className="px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-800 text-xs font-semibold transition-colors inline-flex items-center gap-1"
                        >
                          <span>Manage</span>
                          <span className="text-stone-400">→</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {filteredOrders.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-stone-200 bg-stone-50/50 text-xs text-stone-500">
            <div>
              Showing <span className="font-semibold text-stone-800">{(currentPage - 1) * ordersPerPage + 1}</span> to{' '}
              <span className="font-semibold text-stone-800">
                {Math.min(currentPage * ordersPerPage, filteredOrders.length)}
              </span>{' '}
              of <span className="font-semibold text-stone-800">{filteredOrders.length}</span> orders
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-stone-200 bg-white hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed text-stone-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-stone-700 font-medium">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg border border-stone-200 bg-white hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed text-stone-700"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;